/**
 * Design Library — section patterns (repeatable regions of a design).
 * Pure design knowledge: no pixels, geometry, or editor concepts.
 */

import { definePattern } from './_define';

export const SECTION_PATTERNS = [
  definePattern({
    id: 'feature-row', name: 'Feature Row', family: 'section',
    purpose: 'A row of parallel features or benefits.',
    communicationGoal: 'Show three or four reasons to believe at a glance.',
    visualGoal: 'Even rhythm across equal-weight columns.',
    contentGoal: 'Icon or image, a short title and a line of copy per feature.',
    marketingGoal: 'Convert interest into confidence.',
    idealDocuments: ['Flyer', 'Brochure', 'Presentation', 'Infographic'],
    idealIndustries: ['corporate', 'technology', 'medical', 'education', 'finance'],
    idealAudiences: ['professionals', 'families', 'students'],
    idealGenomes: ['corporate', 'modern', 'swiss', 'bento', 'premium-medical'],
    compatiblePatterns: ['split-hero', 'stat-row', 'premium-cta'],
    compositionPhilosophy: 'Equal columns; consistency is the point.',
    hierarchyPhilosophy: 'All features are peers; the row itself is one unit.',
    typicalSections: ['feature-title', 'feature-copy', 'feature-icon'],
    preferredComponents: ['features'],
    premiumCues: ['precision', 'considered-color'],
    thingsToAvoid: ['uneven-columns', 'clutter'],
    variationRules: ['feature-count', 'icon-or-image'],
    capabilities: ['icon-list', 'multi-column', 'list'],
    tags: ['features', 'row', 'section'],
    expansionRules: { emphasis: 'parity', columns: 'three' },
  }),
  definePattern({
    id: 'stat-row', name: 'Statistic Row', family: 'section',
    purpose: 'A band of headline numbers that prove the claim.',
    communicationGoal: 'Make credibility instantly scannable.',
    visualGoal: 'Big numerals with quiet labels.',
    contentGoal: 'Three or four figures, each with a short label.',
    marketingGoal: 'Turn claims into evidence.',
    idealDocuments: ['Presentation', 'Infographic', 'Brochure', 'Flyer'],
    idealIndustries: ['corporate', 'finance', 'technology', 'education', 'medical'],
    idealAudiences: ['professionals'],
    idealGenomes: ['corporate', 'swiss', 'bento', 'future-ui'],
    compatiblePatterns: ['feature-row', 'split-hero', 'bento-grid'],
    compositionPhilosophy: 'A single horizontal band of equal cells.',
    hierarchyPhilosophy: 'The figure dominates; the label whispers.',
    typicalSections: ['stat-value', 'stat-label'],
    preferredComponents: ['stats'],
    premiumCues: ['confident-scale', 'precision'],
    thingsToAvoid: ['too-many-stats', 'low-contrast'],
    variationRules: ['stat-count', 'label-position'],
    capabilities: ['stats', 'multi-column'],
    tags: ['stats', 'proof', 'section'],
    expansionRules: { emphasis: 'figures', columns: 'three' },
  }),
  definePattern({
    id: 'image-band', name: 'Image Band', family: 'section',
    purpose: 'A full-width image band that resets the eye.',
    communicationGoal: 'Give the design a breath and a mood.',
    visualGoal: 'Edge-to-edge imagery with optional overlay.',
    contentGoal: 'One image, optionally a caption or overlaid line.',
    marketingGoal: 'Sell the feeling, not the facts.',
    idealDocuments: ['Brochure', 'Presentation', 'Flyer', 'Banner'],
    idealIndustries: ['travel', 'hotel', 'restaurant', 'real-estate', 'wedding', 'fashion'],
    idealAudiences: ['general-public', 'tourists', 'luxury-customers'],
    idealGenomes: ['editorial', 'luxury', 'organic', 'luxury-fashion', 'scandinavian'],
    compatiblePatterns: ['minimal-stack', 'editorial-columns', 'magazine-spread'],
    compositionPhilosophy: 'Full-bleed band; the image is the whole statement.',
    hierarchyPhilosophy: 'Image first; any type sits quietly on a scrim.',
    typicalSections: ['image', 'caption'],
    preferredComponents: ['gallery', 'hero'],
    premiumCues: ['generous-whitespace', 'considered-color'],
    thingsToAvoid: ['heavy-gradient-on-text', 'low-quality-imagery'],
    variationRules: ['band-height', 'overlay-strength'],
    capabilities: ['image', 'fullbleed', 'image-led'],
    tags: ['image', 'band', 'section'],
    expansionRules: { emphasis: 'image', width: 'full-bleed' },
  }),
  definePattern({
    id: 'card-stack', name: 'Card Stack', family: 'section',
    purpose: 'Content organised into discrete cards.',
    communicationGoal: 'Chunk information so it is easy to compare.',
    visualGoal: 'Consistent surfaces with gentle depth.',
    contentGoal: 'A title, a line of copy and optional image per card.',
    marketingGoal: 'Make choices feel considered and comparable.',
    idealDocuments: ['Presentation', 'Brochure', 'Flyer', 'Instagram Post'],
    idealIndustries: ['technology', 'education', 'corporate', 'food', 'travel'],
    idealAudiences: ['professionals', 'students', 'families'],
    idealGenomes: ['modern', 'bento', 'glassmorphism', 'playful', 'corporate'],
    compatiblePatterns: ['feature-row', 'pricing-section', 'gradient-hero'],
    compositionPhilosophy: 'Repeated surfaces on a shared grid.',
    hierarchyPhilosophy: 'Cards are peers unless one is deliberately featured.',
    typicalSections: ['card-title', 'card-copy', 'card-image'],
    preferredComponents: ['features', 'gallery'],
    premiumCues: ['subtle-depth', 'crafted-detail'],
    thingsToAvoid: ['inconsistent-cards', 'clutter'],
    variationRules: ['card-count', 'featured-card'],
    capabilities: ['multi-column', 'image', 'list'],
    tags: ['cards', 'stack', 'section'],
    expansionRules: { emphasis: 'parity', surface: 'card' },
  }),
  definePattern({
    id: 'pricing-section', name: 'Pricing Section', family: 'section',
    purpose: 'Tiered pricing presented for comparison and choice.',
    communicationGoal: 'Make the right plan obvious.',
    visualGoal: 'Parallel tiers with one deliberately featured.',
    contentGoal: 'Plan name, price, inclusions and an action per tier.',
    marketingGoal: 'Drive selection of the intended tier.',
    idealDocuments: ['Brochure', 'Presentation', 'Flyer'],
    idealIndustries: ['technology', 'fitness', 'education', 'salon', 'corporate'],
    idealAudiences: ['professionals', 'families', 'students'],
    idealGenomes: ['corporate', 'modern', 'bento', 'glassmorphism'],
    compatiblePatterns: ['card-stack', 'premium-cta', 'feature-row'],
    dependencies: ['premium-cta'],
    compositionPhilosophy: 'Parallel tiers; the featured tier breaks the rhythm.',
    hierarchyPhilosophy: 'Price dominates; the featured tier outranks its peers.',
    typicalSections: ['plan-name', 'price', 'inclusions', 'cta'],
    preferredComponents: ['pricing', 'cta'],
    premiumCues: ['precision', 'considered-color'],
    thingsToAvoid: ['hidden-price', 'too-many-tiers'],
    variationRules: ['tier-count', 'featured-tier-position'],
    capabilities: ['pricing', 'cta', 'multi-column', 'table'],
    tags: ['pricing', 'tiers', 'section'],
    expansionRules: { emphasis: 'featured-tier', columns: 'three' },
  }),
  definePattern({
    id: 'offer-section', name: 'Offer Section', family: 'section',
    purpose: 'A promotional offer given its own commanding region.',
    communicationGoal: 'Make the deal impossible to miss.',
    visualGoal: 'A badge or band that intentionally breaks the grid.',
    contentGoal: 'The offer, its terms and the action.',
    marketingGoal: 'Create urgency and convert.',
    idealDocuments: ['Flyer', 'Poster', 'Instagram Post', 'Banner', 'Email Header'],
    idealIndustries: ['retail', 'restaurant', 'fitness', 'salon', 'travel', 'food'],
    idealAudiences: ['general-public', 'families', 'students'],
    idealGenomes: ['bold', 'playful', 'retro', 'memphis'],
    compatiblePatterns: ['flyer-hero', 'discount-block', 'premium-cta'],
    compositionPhilosophy: 'A deliberate rule-break that the eye cannot skip.',
    hierarchyPhilosophy: 'The offer outranks the headline when the campaign is promotional.',
    typicalSections: ['offer', 'terms', 'cta'],
    preferredComponents: ['cta'],
    premiumCues: ['confident-scale'],
    thingsToAvoid: ['burying-the-offer', 'tiny-cta'],
    variationRules: ['badge-shape', 'offer-placement'],
    capabilities: ['offer', 'cta'],
    tags: ['offer', 'promotion', 'section'],
    expansionRules: { emphasis: 'offer', breakGrid: 'intentional' },
  }),
  definePattern({
    id: 'testimonials', name: 'Testimonials', family: 'section',
    purpose: 'Social proof in the customer’s own words.',
    communicationGoal: 'Let someone else make the claim.',
    visualGoal: 'Quiet, quotable blocks with attribution.',
    contentGoal: 'Quote, name, role or rating.',
    marketingGoal: 'Convert scepticism into trust.',
    idealDocuments: ['Brochure', 'Presentation', 'Flyer', 'Instagram Post'],
    idealIndustries: ['medical', 'salon', 'corporate', 'education', 'real-estate', 'fitness'],
    idealAudiences: ['families', 'professionals', 'general-public'],
    idealGenomes: ['editorial', 'corporate', 'minimal', 'premium-medical'],
    compatiblePatterns: ['feature-row', 'editorial-columns', 'magazine-spread'],
    compositionPhilosophy: 'Restrained blocks; the words carry the weight.',
    hierarchyPhilosophy: 'The quote leads; attribution recedes.',
    typicalSections: ['quote', 'attribution', 'rating'],
    preferredComponents: ['testimonial'],
    premiumCues: ['refined-type', 'restraint'],
    thingsToAvoid: ['stock-cliches', 'fake-sounding-praise'],
    variationRules: ['quote-count', 'avatar-or-not'],
    capabilities: ['testimonial', 'list'],
    tags: ['social-proof', 'quotes', 'section'],
    expansionRules: { emphasis: 'quote', tone: 'quiet' },
  }),
  definePattern({
    id: 'contact-section', name: 'Contact Section', family: 'section',
    purpose: 'The details someone needs to act.',
    communicationGoal: 'Remove every barrier to getting in touch.',
    visualGoal: 'Compact, scannable, unambiguous.',
    contentGoal: 'Phone, email, address, hours, optional map or QR.',
    marketingGoal: 'Close the loop from interest to contact.',
    idealDocuments: ['Flyer', 'Brochure', 'Business Card', 'Poster', 'Pamphlet'],
    idealIndustries: ['medical', 'restaurant', 'salon', 'legal', 'automotive', 'real-estate'],
    idealAudiences: ['general-public', 'families', 'professionals'],
    idealGenomes: ['corporate', 'minimal', 'premium-medical', 'scandinavian'],
    compatiblePatterns: ['qr-block', 'logo-strip', 'flyer-hero'],
    compositionPhilosophy: 'Dense but orderly; every item on one alignment.',
    hierarchyPhilosophy: 'The primary channel leads; the rest support.',
    typicalSections: ['phone', 'email', 'address', 'hours'],
    preferredComponents: ['contact', 'footer'],
    premiumCues: ['precision'],
    thingsToAvoid: ['tiny-type', 'clutter'],
    variationRules: ['channel-count', 'qr-or-not'],
    capabilities: ['contact', 'qr', 'map', 'list'],
    tags: ['contact', 'details', 'section'],
    expansionRules: { emphasis: 'primary-channel', density: 'compact' },
  }),
];
